// =============================================================
// VARIANCE — The Executive Command Center
// File      : Finance.tsx
// Location  : /home/Applications/variance/web/src/pages/Finance.tsx
// Owner     : VARIANCE frontend application only
// Purpose   : Cross-project Executive Financial Analytics.
//             Portfolio budget vs invoiced vs paid, revision drift,
//             invoice funnel, utilization heat, cash readiness gauge.
//             Two-level nested slide-out (Project -> Head breakdown).
// =============================================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, X, ChevronLeft, Banknote, FileText, TrendingUp,
  AlertTriangle, PieChart, BarChart3, Activity, Layers
} from 'lucide-react';
import { PageHelp, HelpTooltip } from '../components/HelpSystem';
import ReactECharts from 'echarts-for-react';

interface FinanceSummary {
  totalBudget: number;
  totalInvoiced: number;
  totalPaid: number;
  totalPending: number;
  totalApproved: number;
  revisionDrift: number;
  projectCount: number;
}

interface ProjectFinanceRow {
  project_id: string;
  project_name: string;
  company_name: string;
  budget: number;
  invoiced: number;
  paid: number;
  pending: number;
  approved: number;
  remaining: number;
  utilization_pct: number;
  revision_total: number;
}

interface HeadBreakdownRow {
  budget_id: string;
  budget_head: string;
  original_amount: number;
  total_revised: number;
  current_budget: number;
  invoiced: number;
  paid: number;
  remaining: number;
  utilization_pct: number;
}

interface RevisionDriftPoint {
  month: string;
  cumulative_revision: number;
}

function getUtilizationColor(pct: number) {
  if (pct > 100) return '#EF4444';
  if (pct >= 85) return '#F59E0B';
  if (pct >= 50) return '#22C55E';
  return '#3B82F6';
}

function getUtilizationLabel(pct: number) {
  if (pct > 100) return 'Over Budget';
  if (pct >= 85) return 'Tight';
  if (pct >= 50) return 'Healthy';
  return 'Under-utilized';
}

export function Finance() {
  const navigate = useNavigate();

  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [projects, setProjects] = useState<ProjectFinanceRow[]>([]);
  const [revisionDrift, setRevisionDrift] = useState<RevisionDriftPoint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedChart, setExpandedChart] = useState<string | null>(null);

  const [selectedProject, setSelectedProject] = useState<ProjectFinanceRow | null>(null);
  const [selectedHead, setSelectedHead] = useState<HeadBreakdownRow | null>(null);
  const [headBreakdown, setHeadBreakdown] = useState<HeadBreakdownRow[]>([]);
  const [isHeadLoading, setIsHeadLoading] = useState(false);

  const fetchData = () => {
    setIsLoading(true);
    Promise.all([
      fetch('/api/cockpit/finance-summary').then(res => res.json()).catch(() => ({})),
      fetch('/api/cockpit/drilldown-finance').then(res => res.json()).catch(() => []),
    ]).then(([summaryRes, projectsRes]) => {
      if (summaryRes && !summaryRes.error) setSummary(summaryRes);
      if (Array.isArray(projectsRes) && !projectsRes.error) {
        setProjects(projectsRes);
      } else if (projectsRes && Array.isArray(projectsRes.projects)) {
        setProjects(projectsRes.projects);
      }

      const drift: RevisionDriftPoint[] = [];
      const src: ProjectFinanceRow[] = Array.isArray(projectsRes)
        ? projectsRes
        : projectsRes?.projects || [];
      let cum = 0;
      src.forEach((p) => {
        cum += Number(p.revision_total || 0);
        drift.push({ month: p.project_name, cumulative_revision: cum });
      });
      setRevisionDrift(drift);

      setIsLoading(false);
    }).catch(() => setIsLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const openProject = async (p: ProjectFinanceRow) => {
    setSelectedProject(p);
    setSelectedHead(null);
    setIsHeadLoading(true);
    setHeadBreakdown([]);
    try {
      const res = await fetch(`/api/cockpit/drilldown-finance/${p.project_id}`);
      if (!res.ok) throw new Error("Not found");
      const data = await res.json();
      if (Array.isArray(data)) setHeadBreakdown(data);
      else if (data && Array.isArray(data.heads)) setHeadBreakdown(data.heads);
    } catch {
      setHeadBreakdown([]); 
    } finally {
      setIsHeadLoading(false);
    }
  };

  const closeProject = () => {
    setSelectedProject(null);
    setSelectedHead(null);
    setHeadBreakdown([]);
  };

  const totalBudget = Number(summary?.totalBudget || 0);
  const totalInvoiced = Number(summary?.totalInvoiced || 0);
  const totalPaid = Number(summary?.totalPaid || 0);
  const totalPending = Number(summary?.totalPending || 0);
  const totalApproved = Number(summary?.totalApproved || 0);
  const revisionDriftTotal = Number(summary?.revisionDrift || 0);

  const cashReadiness = totalBudget > 0
    ? Math.max(0, Math.min(100, ((totalBudget - totalInvoiced) / totalBudget) * 100))
    : 0;

  if (isLoading) return <div className="p-10 text-white">Loading Finance Analytics...</div>;

  return (
    <div className="space-y-6 max-w-[1600px] relative min-h-screen pt-4">

      <PageHelp
        title="Executive Financial Analytics"
        description="Cross-project view of budget, invoicing, payment status, revision drift, and cash readiness. Click any project row to drill into its head-level breakdown."
      />

      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 rounded-full hover:bg-[var(--bg-panel-alt)] transition-colors"
            style={{ color: 'var(--text-secondary)' }}
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>
              Finance
            </h1>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
              Portfolio-level financial analytics across {projects.length} project{projects.length === 1 ? '' : 's'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg border" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
          <Activity size={16} className="text-[var(--brand-accent)]" />
          <span className="text-xs text-gray-400">Live portfolio figures</span>
        </div>
      </div>

      {/* KPI STRIP */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-5 rounded-xl border" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
          <div className="flex justify-between items-start mb-2">
            <span className="text-sm font-medium text-gray-400 flex items-center gap-1">
              Total Budget <HelpTooltip text="Sum of current (revised) budgets across all projects." />
            </span>
            <Banknote size={18} className="text-blue-400" />
          </div>
          <div className="text-3xl font-bold font-mono text-white">₹{(totalBudget / 10000000).toFixed(2)}Cr</div>
        </div>

        <div className="p-5 rounded-xl border" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
          <div className="flex justify-between items-start mb-2">
            <span className="text-sm font-medium text-gray-400 flex items-center gap-1">
              Total Invoiced <HelpTooltip text="Accrual basis — all invoices raised, regardless of payment status." />
            </span>
            <FileText size={18} className="text-orange-400" />
          </div>
          <div className="text-3xl font-bold font-mono text-white">₹{(totalInvoiced / 10000000).toFixed(2)}Cr</div>
        </div>

        <div className="p-5 rounded-xl border" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
          <div className="flex justify-between items-start mb-2">
            <span className="text-sm font-medium text-gray-400 flex items-center gap-1">
              Total Paid <HelpTooltip text="Invoices marked as Paid. Actual cash out the door." />
            </span>
            <TrendingUp size={18} className="text-green-400" />
          </div>
          <div className="text-3xl font-bold font-mono text-white">₹{(totalPaid / 10000000).toFixed(2)}Cr</div>
        </div>

        <div className="p-5 rounded-xl border" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
          <div className="flex justify-between items-start mb-2">
            <span className="text-sm font-medium text-gray-400 flex items-center gap-1">
              Revision Drift <HelpTooltip text="Cumulative value of all budget revisions. High drift indicates scope creep or price escalation." />
            </span>
            <AlertTriangle size={18} className="text-yellow-400" />
          </div>
          <div className={`text-3xl font-bold font-mono ${revisionDriftTotal > 0 ? 'text-yellow-400' : 'text-white'}`}>
            ₹{(revisionDriftTotal / 10000000).toFixed(2)}Cr
          </div>
        </div>
      </div>

      {/* CHARTS ROW 1 */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* BUDGET VS INVOICED VS PAID */}
        <div 
          onClick={() => setExpandedChart('budget_vs_invoiced')}
          className="p-6 rounded-xl border cursor-pointer hover:border-[var(--brand-accent)] transition-colors group relative" 
          style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              Budget vs Invoiced vs Paid
              <HelpTooltip text="Per project. Compare sanctioned budget to what has been billed and what has actually been paid." />
            </h2>
            <span className="text-xs text-gray-500 group-hover:text-[var(--brand-accent)]">Click to expand</span>
          </div>
          <div className="min-h-[350px]">
            <ReactECharts
              option={{
                backgroundColor: 'transparent',
                tooltip: { trigger: 'axis', backgroundColor: '#1A2540', borderColor: '#22304D', textStyle: { color: '#F1F5FB' }, valueFormatter: (val: number) => `₹${(Number(val) / 10000000).toFixed(2)}Cr` },
                legend: { data: ['Budget', 'Invoiced', 'Paid'], textStyle: { color: '#94A3B8', fontSize: 14 }, bottom: 0 },
                grid: { left: '3%', right: '4%', bottom: '15%', top: '10%', containLabel: true },
                xAxis: { type: 'category', data: projects.map(p => p.project_name), axisLabel: { color: '#94A3B8', rotate: 0, fontSize: 12 }, axisLine: { lineStyle: { color: '#22304D' } } },
                yAxis: { type: 'value', axisLabel: { color: '#94A3B8', fontSize: 12, formatter: (val: number) => `${(val / 10000000).toFixed(0)}Cr` }, splitLine: { lineStyle: { color: '#22304D', type: 'dashed' } } },
                series: [
                  { name: 'Budget', type: 'bar', barWidth: '25%', itemStyle: { color: '#3B82F6', borderRadius: [4, 4, 0, 0] }, data: projects.map(p => Number(p.budget || 0)) },
                  { name: 'Invoiced', type: 'bar', barWidth: '25%', itemStyle: { color: '#F97316', borderRadius: [4, 4, 0, 0] }, data: projects.map(p => Number(p.invoiced || 0)) },
                  { name: 'Paid', type: 'bar', barWidth: '25%', itemStyle: { color: '#22C55E', borderRadius: [4, 4, 0, 0] }, data: projects.map(p => Number(p.paid || 0)) }
                ]
              }}
              style={{ height: '100%', width: '100%' }}
            />
          </div>
        </div>

        {/* CUMULATIVE REVISION DRIFT */}
        <div 
          onClick={() => setExpandedChart('revision_drift')}
          className="p-6 rounded-xl border cursor-pointer hover:border-[var(--brand-accent)] transition-colors group relative" 
          style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              Cumulative Revision Drift
              <HelpTooltip text="Cumulative budget revisions across projects. A rising line indicates ongoing scope changes or escalation." />
            </h2>
            <span className="text-xs text-gray-500 group-hover:text-[var(--brand-accent)]">Click to expand</span>
          </div>
          <div className="min-h-[350px]">
            <ReactECharts
              option={{
                backgroundColor: 'transparent',
                tooltip: { trigger: 'axis', backgroundColor: '#1A2540', borderColor: '#22304D', textStyle: { color: '#F1F5FB' }, valueFormatter: (val: number) => `₹${(Number(val) / 10000000).toFixed(2)}Cr` },
                legend: { data: ['Cumulative Revision'], textStyle: { color: '#94A3B8', fontSize: 14 }, bottom: 0 },
                grid: { left: '3%', right: '4%', bottom: '15%', top: '10%', containLabel: true },
                xAxis: { type: 'category', data: revisionDrift.map(d => d.month), axisLabel: { color: '#94A3B8', rotate: 0, fontSize: 12 }, axisLine: { lineStyle: { color: '#22304D' } } },
                yAxis: { type: 'value', axisLabel: { color: '#94A3B8', fontSize: 12, formatter: (val: number) => `${(val / 10000000).toFixed(0)}Cr` }, splitLine: { lineStyle: { color: '#22304D', type: 'dashed' } } },
                series: [{ name: 'Cumulative Revision', type: 'line', smooth: true, symbolSize: 10, itemStyle: { color: '#F59E0B' }, areaStyle: { color: 'rgba(245, 158, 11, 0.15)' }, data: revisionDrift.map(d => d.cumulative_revision) }]
              }}
              style={{ height: '100%', width: '100%' }}
            />
          </div>
        </div>
      </div>

      {/* CHARTS ROW 2 */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* INVOICE STATUS FUNNEL */}
        <div 
          onClick={() => setExpandedChart('funnel')}
          className="p-6 rounded-xl border cursor-pointer hover:border-[var(--brand-accent)] transition-colors group relative" 
          style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              Invoice Status Funnel
              <HelpTooltip text="Invoices move Pending -> Approved -> Paid. A large pending tail may indicate approval bottlenecks." />
            </h2>
            <span className="text-xs text-gray-500 group-hover:text-[var(--brand-accent)]">Click to expand</span>
          </div>
          <div className="min-h-[350px]">
            <ReactECharts
              option={{
                backgroundColor: 'transparent',
                tooltip: { trigger: 'item', backgroundColor: '#1A2540', borderColor: '#22304D', textStyle: { color: '#F1F5FB' }, valueFormatter: (val: number) => `₹${(Number(val) / 10000000).toFixed(2)}Cr` },
                legend: { bottom: 0, textStyle: { color: '#94A3B8', fontSize: 14 } },
                series: [{
                  name: 'Invoice Status', type: 'funnel', left: '10%', right: '10%', top: '10%', bottom: '15%', min: 0, minSize: '10%', maxSize: '100%', sort: 'descending', gap: 2,
                  label: { show: true, color: '#F1F5FB', fontSize: 14, formatter: '{b}' },
                  itemStyle: { borderColor: 'transparent', borderWidth: 0 },
                  data: [
                    { value: totalPending, name: 'Pending', itemStyle: { color: '#F97316' } },
                    { value: totalApproved, name: 'Approved', itemStyle: { color: '#3B82F6' } },
                    { value: totalPaid, name: 'Paid', itemStyle: { color: '#22C55E' } }
                  ]
                }]
              }}
              style={{ height: '100%', width: '100%' }}
            />
          </div>
        </div>

        {/* CASH READINESS */}
        <div 
          onClick={() => setExpandedChart('cash_readiness')}
          className="p-6 rounded-xl border cursor-pointer hover:border-[var(--brand-accent)] transition-colors group relative" 
          style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              Cash Readiness
              <HelpTooltip text="Percentage of budget that remains un-invoiced. High is healthy (runway). Low means the project is close to or over budget." />
            </h2>
            <span className="text-xs text-gray-500 group-hover:text-[var(--brand-accent)]">Click to expand</span>
          </div>
          <div className="min-h-[350px]">
            <ReactECharts
              option={{
                backgroundColor: 'transparent',
                series: [{
                  type: 'gauge', startAngle: 210, endAngle: -30, min: 0, max: 100,
                  progress: { show: true, width: 16, itemStyle: { color: cashReadiness >= 50 ? '#22C55E' : cashReadiness >= 25 ? '#F59E0B' : '#EF4444' } },
                  axisLine: { lineStyle: { width: 16, color: [[1, '#22304D']] } },
                  axisTick: { show: false }, splitLine: { show: false }, axisLabel: { color: '#94A3B8', distance: 20, fontSize: 14 }, pointer: { show: false },
                  detail: { valueAnimation: true, formatter: '{value}%', color: '#F1F5FB', fontSize: 32, fontWeight: 'bold', offsetCenter: [0, '0%'] },
                  data: [{ value: Number(cashReadiness.toFixed(1)) }]
                }]
              }}
              style={{ height: '100%', width: '100%' }}
            />
          </div>
        </div>
      </div>

      {/* EXPANDED CHART MODAL */}
      {expandedChart && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-80 backdrop-blur-sm p-8">
          <div className="w-full max-w-6xl h-[80vh] bg-[var(--bg-panel)] border border-[var(--border-subtle)] rounded-xl p-8 flex flex-col shadow-2xl animate-in zoom-in-95 duration-200 relative">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-white capitalize">
                {expandedChart.replace(/_/g, ' ')}
              </h2>
              <button onClick={() => setExpandedChart(null)} className="p-2 text-gray-400 hover:text-white rounded-full hover:bg-[var(--bg-panel-alt)]">
                <X size={28} />
              </button>
            </div>
            <div className="flex-1">
              {expandedChart === 'budget_vs_invoiced' && (
                <ReactECharts option={{ 
                  backgroundColor: 'transparent',
                  tooltip: { trigger: 'axis', backgroundColor: '#1A2540', borderColor: '#22304D', textStyle: { color: '#F1F5FB' }, valueFormatter: (val: number) => `₹${(Number(val) / 10000000).toFixed(2)}Cr` }, 
                  legend: { textStyle: { color: '#94A3B8', fontSize: 16 }, bottom: 10 }, 
                  grid: { left: '3%', right: '4%', bottom: '10%', top: '5%', containLabel: true }, 
                  xAxis: { type: 'category', data: projects.map(p => p.project_name), axisLabel: { color: '#94A3B8', fontSize: 14 } }, 
                  yAxis: { type: 'value', axisLabel: { color: '#94A3B8', fontSize: 14, formatter: (val: number) => `${(val / 10000000).toFixed(0)}Cr` }, splitLine: { lineStyle: { color: '#22304D', type: 'dashed' } } }, 
                  series: [
                    { name: 'Budget', type: 'bar', itemStyle: { color: '#3B82F6' }, data: projects.map(p => Number(p.budget || 0)) }, 
                    { name: 'Invoiced', type: 'bar', itemStyle: { color: '#F97316' }, data: projects.map(p => Number(p.invoiced || 0)) }, 
                    { name: 'Paid', type: 'bar', itemStyle: { color: '#22C55E' }, data: projects.map(p => Number(p.paid || 0)) }
                  ] 
                }} style={{ height: '100%', width: '100%' }} />
              )}
              {expandedChart === 'revision_drift' && (
                <ReactECharts option={{ 
                  backgroundColor: 'transparent',
                  tooltip: { trigger: 'axis', backgroundColor: '#1A2540', borderColor: '#22304D', textStyle: { color: '#F1F5FB' }, valueFormatter: (val: number) => `₹${(Number(val) / 10000000).toFixed(2)}Cr` }, 
                  legend: { textStyle: { color: '#94A3B8', fontSize: 16 }, bottom: 10 }, 
                  grid: { left: '3%', right: '4%', bottom: '10%', top: '5%', containLabel: true }, 
                  xAxis: { type: 'category', data: revisionDrift.map(d => d.month), axisLabel: { color: '#94A3B8', fontSize: 14 } }, 
                  yAxis: { type: 'value', axisLabel: { color: '#94A3B8', fontSize: 14, formatter: (val: number) => `${(val / 10000000).toFixed(0)}Cr` }, splitLine: { lineStyle: { color: '#22304D', type: 'dashed' } } }, 
                  series: [{ name: 'Cumulative Revision', type: 'line', smooth: true, symbolSize: 12, itemStyle: { color: '#F59E0B' }, areaStyle: { color: 'rgba(245, 158, 11, 0.15)' }, data: revisionDrift.map(d => d.cumulative_revision) }] 
                }} style={{ height: '100%', width: '100%' }} />
              )}
              {expandedChart === 'funnel' && (
                <ReactECharts option={{ 
                  backgroundColor: 'transparent',
                  tooltip: { trigger: 'item', backgroundColor: '#1A2540', borderColor: '#22304D', textStyle: { color: '#F1F5FB' }, valueFormatter: (val: number) => `₹${(Number(val) / 10000000).toFixed(2)}Cr` }, 
                  legend: { textStyle: { color: '#94A3B8', fontSize: 16 }, bottom: 10 }, 
                  series: [{ name: 'Invoice Status', type: 'funnel', left: '10%', right: '10%', top: '10%', bottom: '15%', min: 0, minSize: '10%', maxSize: '100%', sort: 'descending', gap: 2, label: { show: true, color: '#F1F5FB', fontSize: 16, formatter: '{b}' }, itemStyle: { borderColor: 'transparent', borderWidth: 0 }, data: [{ value: totalPending, name: 'Pending', itemStyle: { color: '#F97316' } }, { value: totalApproved, name: 'Approved', itemStyle: { color: '#3B82F6' } }, { value: totalPaid, name: 'Paid', itemStyle: { color: '#22C55E' } }] }] 
                }} style={{ height: '100%', width: '100%' }} />
              )}
              {expandedChart === 'cash_readiness' && (
                <ReactECharts option={{ 
                  backgroundColor: 'transparent',
                  series: [{ type: 'gauge', startAngle: 210, endAngle: -30, min: 0, max: 100, progress: { show: true, width: 20, itemStyle: { color: cashReadiness >= 50 ? '#22C55E' : cashReadiness >= 25 ? '#F59E0B' : '#EF4444' } }, axisLine: { lineStyle: { width: 20, color: [[1, '#22304D']] } }, axisTick: { show: false }, splitLine: { show: false }, axisLabel: { color: '#94A3B8', distance: 20, fontSize: 16 }, pointer: { show: false }, detail: { valueAnimation: true, formatter: '{value}%', color: '#F1F5FB', fontSize: 48, fontWeight: 'bold', offsetCenter: [0, '0%'] }, data: [{ value: Number(cashReadiness.toFixed(1)) }] }] 
                }} style={{ height: '100%', width: '100%' }} />
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
